/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import forEach from 'lodash/forEach';
import includes from 'lodash/includes';

/**
 * Look up a campsite-level release schedule by reservation window.
 * late_releases → Days, other_releases → Months.
 *
 * @param {object} releaseDates - campground release data from /releases
 * @param {windowValue: string|number|null, windowUnit: string|null} options
 * @returns {object|undefined}
 */
export const getCampsiteReleaseSchedule = (releaseDates, { windowValue, windowUnit }) => {
    if (windowValue == null || !releaseDates) {
        return undefined;
    }

    const isMonthsWindow = includes(['Months', 'Month'], windowUnit);
    const dayBasedReleases = releaseDates.late_releases;
    const monthBasedReleases = releaseDates.other_releases;
    const preferredMap = isMonthsWindow ? monthBasedReleases : dayBasedReleases;
    const fallbackMap = isMonthsWindow ? dayBasedReleases : monthBasedReleases;

    return preferredMap?.[windowValue] ?? fallbackMap?.[windowValue];
};

/**
 * compile release data by campsites via the original release data from the /releases endpoint
 * @param {object} releaseData - campground release data from /releases
 */
export const compileCurReleaseDataByCampsite = ({ releasesData }) => {
    const {
        current_release: facilityLevelRelease,
        late_releases: dayBasedReleases,
        other_releases: monthBasedReleases,
    } = releasesData;

    // (1) establish the default FACILITY-level release dates with key 'default'
    const releaseDataByCampsites = {
        default: {
            releaseStart: facilityLevelRelease.release_time,
            releaseEnd: facilityLevelRelease.end,
            slidingEnd: facilityLevelRelease.sliding_end,
            groupReleaseStart: facilityLevelRelease.group_release_time,
            groupReleaseEnd: facilityLevelRelease.group_end,
            groupSlidingEnd: facilityLevelRelease.group_sliding_end,
        },
    };

    // (2) establish individual CAMPSITE-level release dates with each campsite id as key
    const applySiteLevelReleases = (siteLevelReleases) => {
        forEach(siteLevelReleases, (releaseSchedule) => {
            const { sites, current_release: siteLevelRelease } = releaseSchedule;

            forEach(sites, (site) => {
                const { campsite_id: campsiteId, campsite_type: campsiteType } = site;

                const isGroupSite = includes(campsiteType, 'GROUP');

                releaseDataByCampsites[campsiteId] = {
                    releaseStart: isGroupSite
                        ? siteLevelRelease.group_release_time
                        : siteLevelRelease.release_time,
                    releaseEnd: isGroupSite
                        ? siteLevelRelease.group_end
                        : siteLevelRelease.end,
                    slidingEnd: isGroupSite
                        ? siteLevelRelease.group_sliding_end
                        : siteLevelRelease.sliding_end,
                };
            });
        });
    };

    applySiteLevelReleases(dayBasedReleases);
    applySiteLevelReleases(monthBasedReleases);

    return releaseDataByCampsites;
};
